import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle, Circle, Loader2, XCircle, SkipForward } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ScanStageKey, StageStatus } from '../../types'
import { STAGE_ORDER } from '../../types'
import { BRAND } from '../../config'

interface StageState {
  key: ScanStageKey
  status: StageStatus
  message: string
}

interface StageChecklistProps {
  stages: Record<ScanStageKey, StageState>
  className?: string
}

function StageIcon({ status }: { status: StageStatus }) {
  switch (status) {
    case 'completed':
      return <CheckCircle className="w-4 h-4 text-green-600 shrink-0" strokeWidth={2} />
    case 'running':
      return <Loader2 className="w-4 h-4 shrink-0 animate-spin" style={{ color: BRAND.navy }} />
    case 'failed':
      return <XCircle className="w-4 h-4 text-red-500 shrink-0" />
    case 'skipped':
      return <SkipForward className="w-4 h-4 text-[#99a1af] shrink-0" />
    default:
      return <Circle className="w-4 h-4 text-[#d1d5db] shrink-0" strokeWidth={1.5} />
  }
}

/**
 * StageChecklist — sidebar progress list for the scan experience.
 * Stage labels are translated via the 'scoring' namespace.
 */
export function StageChecklist({ stages, className = '' }: StageChecklistProps) {
  const { t } = useTranslation('scoring')

  return (
    <div className={`space-y-5 ${className}`}>
      {STAGE_ORDER.map((key) => {
        const stage = stages[key]
        const status = stage?.status ?? 'pending'
        const isActive = status === 'running'
        const isDone = status === 'completed' || status === 'skipped'

        return (
          <motion.div
            key={key}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-start gap-3"
          >
            <div className="mt-0.5">
              <StageIcon status={status} />
            </div>
            <div className="flex-1 min-w-0">
              <span
                className={`text-sm font-medium leading-5 block ${
                  isActive
                    ? 'text-[#101828]'
                    : isDone
                      ? 'text-[#6a7282]'
                      : 'text-[#99a1af]'
                }`}
              >
                {t(`stage.${key}`)}
              </span>
              <AnimatePresence>
                {isActive && stage?.message && (
                  <motion.p
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-xs text-[#6a7282] mt-0.5 truncate leading-4"
                  >
                    {stage.message}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        )
      })}
    </div>
  )
}
